import { useEffect, useRef } from 'react';
import { Box } from '@mantine/core';
import { COLORS } from '@/utils/GlobalStyles';

interface WaveformVisualizerProps {
  analyser: AnalyserNode | null;
  isRecording: boolean;
}

function WaveformVisualizer({
  analyser,
  isRecording,
}: WaveformVisualizerProps) {
  const barsRef = useRef<HTMLDivElement>(null);
  const animationId = useRef<number>();
  const dataArray = useRef<Uint8Array>();

  useEffect(() => {
    if (!analyser || !isRecording) return;

    const bufferLength = analyser.frequencyBinCount;
    dataArray.current = new Uint8Array(bufferLength);
    initializeBars();
    startVisualization();

    return () => {
      cancelAnimationFrame(animationId.current!);
      if (barsRef.current) {
        barsRef.current.innerHTML = '';
      }
    };
  }, [analyser, isRecording]);

  const initializeBars = () => {
    const barsContainer = barsRef.current;
    if (!barsContainer || !dataArray.current) return;

    if (!barsContainer.hasChildNodes()) {
      for (let i = 0; i < dataArray.current.length; i++) {
        const bar = document.createElement('div');
        bar.style.backgroundColor = COLORS.primary;
        bar.style.width = '5px';
        bar.style.borderRadius = '10px';
        bar.style.transition = 'height 0.1s ease';
        barsContainer.appendChild(bar);
      }
    }
  };

  const startVisualization = () => {
    if (!analyser || !dataArray.current) return;

    const bars = barsRef.current?.childNodes;
    if (!bars) return;

    const minHeight = 5;
    const maxHeight = 93;

    const renderFrame = () => {
      animationId.current = requestAnimationFrame(renderFrame);
      analyser.getByteFrequencyData(dataArray.current!);

      for (let i = 0; i < dataArray.current!.length; i++) {
        let barHeight = (dataArray.current![i] / 64) * 100;
        barHeight = Math.min(Math.max(barHeight, minHeight), maxHeight);
        (bars[i] as HTMLElement).style.height = `${barHeight}px`;
      }
    };

    renderFrame();
  };

  return (
    <Box
      ref={barsRef}
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        gap: 2,
        alignItems: 'center',
        height: 93,
        alignContent: 'flex-start',
        justifyContent: 'space-between',
        overflow: 'hidden',
      }}
    />
  );
}

export default WaveformVisualizer;
